import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { UseCaseDetail } from '../../types';
import Input from '../Forms/Input';
import Textarea from '../Forms/Textarea';
import Select from '../Forms/Select';

interface UseCaseFormProps {
  onSubmit: (data: UseCaseDetail) => void;
  initialData?: UseCaseDetail;
}

const UseCaseForm: React.FC<UseCaseFormProps> = ({ onSubmit, initialData }) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UseCaseDetail>({
    defaultValues: initialData || {
      id: '', // Important pour la logique de sauvegarde
      name: '',
      shortDescription: '', // Hérité mais peut être édité ici aussi si besoin
      description: '',
      whyComment: '',
      techElements: '',
      valueScore: 0,
      complexityScore: 0,
      status: 'Identified',
      evaluation: [{ dimension: '', score: 0, comment: '' }],
      analysis: { strengths: [''], weaknesses: [''] },
      recommendation: '',
    },
  });

  const {
    fields: evaluationFields,
    append: appendEvaluation,
    remove: removeEvaluation,
  } = useFieldArray({
    control,
    name: 'evaluation',
  });

  const {
    fields: strengthFields,
    append: appendStrength,
    remove: removeStrength,
  } = useFieldArray({ // Retirer le type explicite, laisser l'inférence
    control,
    name: 'analysis.strengths',
  });

  const {
    fields: weaknessFields,
    append: appendWeakness,
    remove: removeWeakness,
  } = useFieldArray({ // Retirer le type explicite, laisser l'inférence
    control,
    name: 'analysis.weaknesses',
  });

  const statusOptions = [
    { value: 'Identified', label: t('useCases.identified') },
    { value: 'Qualified', label: t('useCases.qualified') },
    { value: 'Prioritized', label: t('useCases.prioritized') },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label={t('useCases.useCaseName')}
        name="name"
        register={register}
        rules={{ required: t('useCases.nameRequired') }}
        error={errors.name}
      />
      <Textarea
        label={t('useCases.detailedDescription')}
        name="description"
        register={register}
        error={errors.description}
      />
      <Textarea
        label={t('useCases.whyComment')}
        name="whyComment"
        register={register}
        error={errors.whyComment}
      />
      <Textarea
        label={t('useCases.technicalElements')}
        name="techElements"
        register={register}
        error={errors.techElements}
      />
       <Input
        label={t('useCases.valueScore')}
        name="valueScore" // name est requis par Input
        type="number"
        register={register} // Passer register explicitement
        rules={{ valueAsNumber: true, min: 0 }}
        error={errors.valueScore}
      />
       <Input
        label={t('useCases.complexityScore')}
        name="complexityScore"
        type="number"
        register={register}
        rules={{ valueAsNumber: true, min: 0 }}
        error={errors.complexityScore}
      />

      <Select
        label={t('useCases.status')}
        name="status"
        register={register}
        options={statusOptions}
        error={errors.status}
      />

      <div className="space-y-2 border p-3 rounded">
        <h3 className="font-semibold">{t('useCases.detailedEvaluation')}</h3>
        {evaluationFields.map((item, index) => (
          <div key={item.id} className="flex items-start space-x-2 border-b pb-2 mb-2">
            <div className="flex-grow space-y-1">
               <Input
                label={t('useCases.dimension', { n: index + 1 })}
                name={`evaluation.${index}.dimension`}
                register={register}
                rules={{ required: true }}
                error={errors.evaluation?.[index]?.dimension}
                placeholder={t('useCases.impactBusiness')}
               />
               <Input
                 label={t('useCases.score', { n: index + 1 })}
                 name={`evaluation.${index}.score`}
                 type="number"
                 register={register}
                 rules={{ valueAsNumber: true, required: true, min: 0 }}
                 error={errors.evaluation?.[index]?.score}
                 placeholder={t('useCases.score', { n: index + 1 })}
               />
               <Textarea
                 label={t('useCases.comment', { n: index + 1 })}
                 name={`evaluation.${index}.comment`}
                 register={register}
                 error={errors.evaluation?.[index]?.comment}
                 rows={2}
               />
             </div>
             <button type="button" onClick={() => removeEvaluation(index)} className="px-2 py-1 text-sm text-red-700 border border-red-300 rounded hover:bg-red-50">
               {t('useCases.remove')}
             </button>
           </div>
         ))}
         <button type="button" onClick={() => appendEvaluation({ dimension: '', score: 0, comment: '' })} className="px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
           {t('useCases.addDimension')}
         </button>
      </div>

      <div className="space-y-2 border p-3 rounded">
        <h3 className="font-semibold">{t('useCases.analysisStrengthsWeaknesses')}</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-medium">{t('useCases.strengths')}</h4>
          {strengthFields.map((item, index) => (
            <div key={item.id} className="flex items-center space-x-2">
              <Input
                // label n'est pas nécessaire ici, le placeholder suffit
                label=""
                name={`analysis.strengths.${index}`}
                register={register}
                rules={{ required: true }}
                error={errors.analysis?.strengths?.[index]}
                placeholder={t('useCases.strengthN', { n: index + 1 })}
                className="flex-grow"
              />
              <button type="button" onClick={() => removeStrength(index)} className="px-2 py-1 text-sm text-red-700 border border-red-300 rounded hover:bg-red-50">
                -
              </button>
            </div>
          ))}
          <button type="button" onClick={() => appendStrength('' as any)} className="px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
            {t('useCases.addStrength')}
          </button>
        </div>
         <div className="space-y-1">
          <h4 className="text-sm font-medium">{t('useCases.weaknesses')}</h4>
          {weaknessFields.map((item, index) => (
            <div key={item.id} className="flex items-center space-x-2">
              <Input
                label=""
                name={`analysis.weaknesses.${index}`}
                register={register}
                rules={{ required: true }}
                error={errors.analysis?.weaknesses?.[index]}
                placeholder={t('useCases.weaknessN', { n: index + 1 })}
                className="flex-grow"
              />
              <button type="button" onClick={() => removeWeakness(index)} className="px-2 py-1 text-sm text-red-700 border border-red-300 rounded hover:bg-red-50">
                -
              </button>
            </div>
          ))}
          <button type="button" onClick={() => appendWeakness('' as any)} className="px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
            {t('useCases.addWeakness')}
          </button>
        </div>
      </div>

      <Textarea
        label={t('useCases.recommendation')}
        name="recommendation"
        register={register}
        error={errors.recommendation}
      />

      <button type="submit" className="px-4 py-2 font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
        {t('useCases.save')}
      </button>
    </form>
  );
};

export default UseCaseForm;
